import React, { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { SyncModelCvrSnapshotSummarySchema } from "@/domains/openapi/types";
import { Uuid } from "@/domains/global/identifiers";
import { SyncModelCvrSnapshotsTable } from "@/pages/sync-cvrs/view/sync-model-cvr-snapshots-table";

export const SyncCvrsViewPage: FC = () => {
  const navigate = useNavigate();
  const { syncCvrId } = useParams<{ syncCvrId: Uuid }>();

  if (!syncCvrId) {
    return <div>Invalid ID</div>;
  }

  const pageSize = 100;

  const syncCvrResult = useQuery({
    queryKey: ["sync-cvr", syncCvrId],
    queryFn: () =>
      api.get("/internal/v1/sync-cvrs/{sync_cvr_id}", {
        params: {
          path: {
            sync_cvr_id: syncCvrId,
          },
        },
      }),
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["sync-model-cvr-snapshots", syncCvrId],
    queryFn: () =>
      api.post("/internal/v1/sync-model-cvr-snapshots/search", {
        body: {
          filter_by_cvr_id: syncCvrId,
        },
      }),
  });

  const handleViewSyncModelCvrSnapshot = (id: string) => {
    navigate(
      atlasRoutes.syncModelCvrSnapshotsView({ params: { syncModelCvrSnapshotId: id } }).path
    );
  };

  const tableData = useMemo<SyncModelCvrSnapshotSummarySchema[]>(
    () => data?.data?.results ?? [],
    [data]
  );

  return (
    <Container>
      <h1>Sync CVRS</h1>
      <pre>{JSON.stringify(syncCvrResult.data, null, 2)}</pre>

      <h1>Sync CVR Snapshots</h1>
      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching sync CVRS: {error.message}</div>}

      {/* Table or No Results */}
      {tableData.length > 0 ? (
        <SyncModelCvrSnapshotsTable
          data={tableData}
          onViewSyncModelCvrSnapshot={handleViewSyncModelCvrSnapshot}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && <div>No sync model CVR snapshots found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;
